import { memo, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';

import { useTrackClickedEllipsisMenuButton } from '@air/analytics';
import { type Board, type PublicBoard } from '@air/api/types';
import { EllipsisIcon } from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { itemIsSelectedSelector } from '@air/redux-selected-items';

import { CardAction } from '~/components/CardAction';
import { CardEllipsisMenu } from '~/components/Gallery/CardEllipsisMenu';
import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { type GetItemMenuOptions } from '~/constants/MenuOptions';
import { BOARD_CARD_MENU, BOARD_CARD_MENU_BUTTON } from '~/constants/testIDs';
import { useAirSelector } from '~/utils/ReduxUtils';

export type BoardGalleryCardProps<C> = {
  board: C;
  getItemMenuOptions: GetItemMenuOptions;
  getSelectionMenuOptions: GetSelectionMenuOptionsType;
  setIsMenuOpen: (value: boolean) => void;
};

const _BoardGalleryCardMenu = <C extends Board | PublicBoard>({
  board,
  getItemMenuOptions,
  getSelectionMenuOptions,
  setIsMenuOpen,
}: BoardGalleryCardProps<C>) => {
  const { trackClickedEllipsisMenuButton } = useTrackClickedEllipsisMenuButton();
  const isSelected = useAirSelector((st) => itemIsSelectedSelector(st, board.id));

  const selectionMenuOptions = getSelectionMenuOptions('gallery-multi-select-ellipsis-menu');
  const ellipsisMenuOptions = getItemMenuOptions({ trackLocation: 'gallery-item-ellipsis-menu' });

  const onMenuChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        trackClickedEllipsisMenuButton({ location: 'asset-item' });
      }

      flushSync(() => {
        setIsMenuOpen(isOpen);
      });
    },
    [setIsMenuOpen, trackClickedEllipsisMenuButton],
  );

  const menuOptions = useMemo(() => {
    const menuOptionsArray: DropdownMenuOption[] = [];

    if (isSelected && selectionMenuOptions.length) {
      menuOptionsArray.push(...selectionMenuOptions);
    } else {
      menuOptionsArray.push(...ellipsisMenuOptions);
    }

    return menuOptionsArray;
  }, [ellipsisMenuOptions, isSelected, selectionMenuOptions]);

  if (!menuOptions?.length) {
    return null;
  }

  return (
    <CardEllipsisMenu
      title="More board options"
      menuProps={{ options: menuOptions }}
      onMenuChange={onMenuChange}
      actionSheetTestId={BOARD_CARD_MENU}
      trigger={<CardAction data-testid={BOARD_CARD_MENU_BUTTON} icon={<EllipsisIcon size="sm" />} />}
    />
  );
};

// biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
export const BoardGalleryCardMenu = memo(_BoardGalleryCardMenu) as typeof _BoardGalleryCardMenu;
